import { Controller } from "@hotwired/stimulus"

/*
  Behaviour:
  - Blank input => ignored (doesn't affect overall)
  - 0 => counts as 0
  - Clamp to 0..100 on blur
  - Allow up to 2dp
  - Overall = Σ(score * weight) / Σ(weight) across ENTERED scores only
  - Display 2dp in overall, and "—" if nothing entered
*/

const PASS_FAIL_SCORES = new Map([["Pass", 100], ["Fail", 0]])

export default class extends Controller {
  static targets = ["score", "overall"]

  connect() {
    this.recalculateAll();
  }

  recalculate(event) {
    const input = event?.target;
    if (!input) return;

    const pupilId = input.dataset.pupilId;
    if (!pupilId) return;

    this.updateOverallForPupil(pupilId);
  }

  sanitize(event) {
    const input = event?.target;
    if (!input) return;

    const pupilId = input.dataset.pupilId;
    if (!pupilId) return;

    const raw = (input.value ?? "").toString().trim();
    if (raw === "") {
      this.updateOverallForPupil(pupilId);
      return;
    }

    const score = Number(raw);
    if (Number.isNaN(score)) {
      input.value = "";
      this.updateOverallForPupil(pupilId);
      return;
    }

    const clamped = Math.min(100, Math.max(0, score));
    const rounded = Math.round(clamped * 100) / 100;

    input.value = this.formatUpTo2dp(rounded);
    this.updateOverallForPupil(pupilId);
  }

  recalculateAll() {
    const pupilIds = new Set(this.scoreTargets.map((el) => el.dataset.pupilId).filter(Boolean));
    pupilIds.forEach((id) => this.updateOverallForPupil(id));
  }

  updateOverallForPupil(pupilId) {
    const overallCell = this.overallTargets.find((el) => el.dataset.pupilId === String(pupilId));
    if (!overallCell) return;

    const inputs = this.scoreTargets.filter((el) => el.dataset.pupilId === String(pupilId));

    const { overallBp, hasAnyScore } = this.computeOverallBasisPoints(inputs);

    const inner = overallCell.querySelector("div") || overallCell;

    if (!hasAnyScore) {
      inner.textContent = "—";
      return;
    }

    inner.innerHTML = `${this.formatUpTo2dp(overallBp / 100)}<span class="ml05">%</span>`;
  }

  computeOverallBasisPoints(inputs) {
    // Using basis points (bp) to avoid float drift:
    // scoreBp  = score% * 100
    // weightBp = weight% * 100
    //
    // contributionBp = round(scoreBp * weightBp / 10000)
    // overallBp = Σ(contributionBp)

    let overallSumBp = 0n;
    let hasAnyScore = false;

    for (const el of inputs) {
      if (el.type === "radio" && !el.checked) continue;

      const scoreRaw = (el.value ?? "").toString().trim();
      if (scoreRaw === "") continue;

      const scoreBp = this.parseScoreToBp(scoreRaw);
      if (scoreBp === null) continue;

      const weightBp = this.parseWeightToBp(el.dataset.courseworkWeight);
      if (weightBp === null || weightBp === 0n) continue;

      hasAnyScore = true;

      const numerator = scoreBp * weightBp;
      const contributionBp = (numerator + 5000n) / 10000n;

      overallSumBp += contributionBp;
    }

    if (!hasAnyScore) {
      return { overallBp: 0, hasAnyScore: false };
    }

    return { overallBp: Number(overallSumBp), hasAnyScore: true };
  }

  parseScoreToBp(raw) {
    const score = PASS_FAIL_SCORES.has(raw) ? PASS_FAIL_SCORES.get(raw) : Number(raw);
    if (Number.isNaN(score)) return null;

    const clamped = Math.min(100, Math.max(0, score));
    return BigInt(Math.round(clamped * 100));
  }

  parseWeightToBp(raw) {
    const weightString = (raw ?? "").toString().trim();
    if (weightString === "") return null;

    const weight = Number(weightString);
    if (Number.isNaN(weight)) return null;

    return BigInt(Math.round(weight * 100));
  }

  formatUpTo2dp(number) {
    const formattedNumber = Number(number).toFixed(2);
    return formattedNumber.replace(/\.?0+$/, "");
  }
}
